/**
 * The Files tab's "Context repos": git repositories cloned into `context/<name>` only for the agent and search to read
 * (vfs-service `/context-repos`). They are read-only and never built from birth, never a service (not in Apps, Start
 * all or Publish); their remote is kept, so Refresh pulls the latest (fast-forward only). Each row: name, URL, branch,
 * short sha and badges, Refresh and, for the project's owner, Remove (asked first, naming the repo).
 * "Add repo as context…" opens the "Start from git" form (URL, a name read from it, the account for its host, errors
 * with Connect) aimed at `context/`.
 */
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { ConfigProvider, Modal, theme as antdTheme } from 'antd';
import * as vfsClient from '../../utils/vfsClient';
import useRepoFlags from '../../hooks/useRepoFlags';
import StartFromGit from '../sandbox/StartFromGit';
import RepoFlagBadges from '../repoFlags/RepoFlagBadges';

const CONTEXT_FLAGS = { readOnly: true, noBuild: true, context: true };

/** "Add repo as context…": the clone form for `context/<name>`, in its own dialog. */
export function AddContextRepoDialog({ open, onClose, projectId, takenNames = [], onAdded = null }) {
  const isDark = typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme') === 'dark';
  return (
    <ConfigProvider theme={{ algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm }}>
      <Modal className="sbx-new-project sbx-git-dialog" destroyOnHidden footer={null} onCancel={onClose} open={open} title="Add repo as context" width={560}>
        <StartFromGit
          clone={(id, { repoUrl, name, account }) => vfsClient.addContextRepo(id, { repoUrl, name, account })}
          ensureProject={async() => projectId}
          folder="context"
          hint={<p className="sbx-new-project-hint">The repository is cloned into <code>context/</code> with its remote kept, so it can be refreshed. The agent reads it and search finds it; it is never edited, built or pushed. Private repositories use your connected account for their server.</p>}
          onCancel={onClose}
          onCloned={() => onAdded?.()}
          onFinished={() => onClose()}
          submitLabel="Add as context"
          takenNames={takenNames}
        />
      </Modal>
    </ConfigProvider>
  );
}

AddContextRepoDialog.propTypes = {
  onAdded: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  projectId: PropTypes.string.isRequired,
  // The context repos' names already used.
  takenNames: PropTypes.arrayOf(PropTypes.string),
};

export default function ContextRepos({ projectId, canEdit = true }) {
  const { canChange, reload: reloadFlags } = useRepoFlags(projectId);
  const [repos, setRepos] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState({}); // name -> 'refresh' | 'remove'
  const [notes, setNotes] = useState({}); // name -> { ok, text }
  const [removing, setRemoving] = useState(null); // the repo asked about
  const [removeError, setRemoveError] = useState(null);

  const load = useCallback(async() => {
    try {
      setRepos(await vfsClient.listContextRepos(projectId));
      setLoadError(null);
    } catch (failure) {
      // An answer that says why is shown; no answer at all (the sandbox out of reach) leaves the list empty.
      setLoadError(failure?.status > 0 ? failure.message || 'The context repos could not be read' : null);
      setRepos((now) => now || []);
    }
  }, [projectId]);
  useEffect(() => { setRepos(null); load(); }, [load]);

  const note = (name, value) => setNotes((now) => ({ ...now, [name]: value }));
  const busyFor = (name, value) => setBusy((now) => ({ ...now, [name]: value }));

  const refresh = async(repo) => {
    busyFor(repo.name, 'refresh');
    note(repo.name, null);
    try {
      const answer = await vfsClient.refreshContextRepo(projectId, repo.name);
      const sha = String(answer?.sha || '').slice(0, 7);
      note(repo.name, { ok: true, text: answer?.summary || (answer?.updated ? `Updated to ${sha}.` : 'Already up to date.') });
      load();
    } catch (failure) {
      note(repo.name, { ok: false, text: failure?.message || 'It could not be refreshed.' });
    } finally {
      busyFor(repo.name, null);
    }
  };

  const remove = async() => {
    const repo = removing;
    busyFor(repo.name, 'remove');
    setRemoveError(null);
    try {
      await vfsClient.removeContextRepo(projectId, repo.name);
      setRemoving(null);
      load();
      reloadFlags();
    } catch (failure) {
      setRemoveError(failure?.message || 'It could not be removed.');
    } finally {
      busyFor(repo.name, null);
    }
  };

  if (repos === null) { return null; }
  if (repos.length === 0 && !canEdit && !loadError) { return null; }
  return (
    <section aria-label="Context repos" className="files-context" data-testid="files-context">
      <div className="files-context-head">
        <h3>Context repos</h3>
        {canEdit && <button className="files-btn" data-testid="files-add-context" onClick={() => setAdding(true)} type="button">Add repo as context…</button>}
      </div>
      <p className="files-hint">Read by the agent and searchable; never edited, built or pushed.</p>
      {loadError && (
        <div className="files-error" role="alert">
          <span>The context repos could not be read: {loadError}</span>
          <button className="files-btn" onClick={load} type="button">Try again</button>
        </div>
      )}
      {repos.length === 0 && !loadError && <p className="files-empty files-context-none">No context repo yet.</p>}
      {repos.length > 0 && (
        <ul className="files-context-list">
          {repos.map((repo) => (
            <li className="files-context-row" data-testid={`files-context-${repo.name}`} key={repo.name}>
              <div className="files-context-main">
                <strong>{repo.name}</strong>
                <RepoFlagBadges flags={repo.flags || CONTEXT_FLAGS} />
                <div className="files-dir">
                  {repo.url || 'no remote'}
                  {repo.branch && <> · {repo.branch}</>}
                  {repo.sha && <> · <code title={repo.sha}>{String(repo.sha).slice(0, 7)}</code></>}
                </div>
                {notes[repo.name] && <div className={notes[repo.name].ok ? 'files-dir' : 'repo-flag-error'} role={notes[repo.name].ok ? 'status' : 'alert'}>{notes[repo.name].text}</div>}
              </div>
              <div className="files-row-actions">
                {canEdit && (
                  <button aria-label={`Refresh ${repo.name}`} className="files-btn" disabled={Boolean(busy[repo.name]) || !repo.url} onClick={() => refresh(repo)} title={repo.url ? 'Pull the latest from its remote' : 'It has no remote'} type="button">
                    {busy[repo.name] === 'refresh' ? 'Refreshing…' : 'Refresh'}
                  </button>
                )}
                {canChange && (
                  <button aria-label={`Remove ${repo.name}`} className="files-btn is-danger" disabled={Boolean(busy[repo.name])} onClick={() => { setRemoveError(null); setRemoving(repo); }} type="button">Remove</button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
      {adding && (
        <AddContextRepoDialog
          onAdded={() => { load(); reloadFlags(); }}
          onClose={() => setAdding(false)}
          open
          projectId={projectId}
          takenNames={repos.map((repo) => repo.name)}
        />
      )}
      {removing && (
        <Modal
          cancelText="Cancel"
          confirmLoading={busy[removing.name] === 'remove'}
          okButtonProps={{ danger: true }}
          okText="Remove"
          onCancel={() => { if (!busy[removing.name]) { setRemoving(null); } }}
          onOk={remove}
          open
          title={`Remove context/${removing.name}?`}
        >
          <p>The folder <strong>context/{removing.name}</strong> leaves the project: the agent no longer reads it and search no longer finds it. The repository on its server is not touched.</p>
          {removeError && <p className="repo-flag-error" role="alert">{removeError}</p>}
        </Modal>
      )}
    </section>
  );
}

ContextRepos.propTypes = {
  // Viewers see the list only.
  canEdit: PropTypes.bool,
  projectId: PropTypes.string.isRequired,
};
